import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePageMeta } from '../../publicSite/usePageMeta';

const Icons = {
  Crown: () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
    </svg>
  ),
  Eye: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  EyeOff: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ),
  User: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="7" r="4"/>
      <path d="M5.5 21a6.5 6.5 0 0 1 13 0"/>
    </svg>
  ),
  Register: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 3h4a2 2 0 0 1 2 2v4"/>
      <path d="M10 14L21 3"/>
      <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"/>
    </svg>
  )
};

export default function Login() {
  usePageMeta('Sign In | School of Shem', 'Sign in to the School of Shem learning portal.');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await login(email, password);
      const landingRoutes = {
        student: '/student/dashboard',
        teacher: '/teacher/dashboard',
        admin: '/admin/dashboard'
      };
      navigate(landingRoutes[user.activeRole || user.role] || '/');
    } catch (err) {
      if (['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found'].includes(err?.code)) {
        setError('The email address or password is incorrect.');
      } else if (err?.code === 'auth/too-many-requests') {
        setError('Too many sign-in attempts. Please wait a moment and try again.');
      } else if (err?.code === 'auth/network-request-failed') {
        setError('Check your connection and try signing in again.');
      } else {
        setError('Sign in is unavailable right now. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="login-page" id="main-content" tabIndex="-1">
      <div className="login-container">
        <div className="login-logo">
          <div className="login-logo-icon">
            <Icons.Crown />
          </div>
          <h1 className="login-title">School of SHEM</h1>
          <p className="login-subtitle">A Kingdom-based economic coaching and learning experience</p>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '1.25rem', fontFamily: 'var(--font-serif)', marginBottom: '8px' }}>
            Welcome to School of SHEM Portal
          </h2>
          <p style={{ color: 'var(--gray-500)', fontSize: '0.9375rem' }}>
            Sign in to track attendance and access your private classroom
          </p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          {error && (
            <div role="alert" id="login-error"
              style={{
                padding: '12px 16px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: 'var(--danger)',
                fontSize: '0.875rem',
                marginBottom: '20px'
              }}>
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="login-email">Email Address</label>
            <input
              id="login-email"
              type="email"
              className="form-input"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              aria-describedby={error ? 'login-error' : undefined}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="login-password">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                aria-describedby={error ? 'login-error' : undefined}
                required
              />
              <button
                type="button"
                className="login-password-toggle"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary w-full"
            disabled={isLoading}
            style={{ marginTop: '8px' }}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={{ display: 'grid', gap: '12px', marginTop: '20px' }}>
          <button
            type="button"
            className="btn btn-secondary w-full"
            onClick={() => navigate('/student-signup')}
          >
            Student Sign Up
          </button>
          <button
            type="button"
            className="btn btn-outline w-full"
            onClick={() => navigate('/')}
          >
            Back to Home
          </button>
        </div>
      </div>
    </main>
  );
}
